import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { branding } from "../config/branding";

// ============================================================================
// TYPES
// ============================================================================

interface TimelineOptin {
  date: string; source: string; medium: string; campaign: string; content: string; sheet: string;
}
interface TimelineApplication {
  date: string; source: string; medium: string; campaign: string; content: string; responseType: string;
}
interface TimelineCall {
  date: string; outcome: string; closer: string; setter: string; cash: number; offer: string;
  notes: string; fathomLink: string; platform: string; funnel: string;
}
interface TimelineWebinarOptin {
  date: string; webinarName: string; webinarId: string; source: string; medium: string; campaign: string;
}
interface TimelineMeetingBooking {
  bookedAt: string; meetingAt: string; closer: string;
}
interface LeadTimelineData {
  email: string; name: string; phone: string; country: string; leadType: string;
  profile: { hasBrand: string; currentMRR: string; ableToInvest: string };
  optins: TimelineOptin[];
  application: TimelineApplication | null;
  calls: TimelineCall[];
  webinarOptins: TimelineWebinarOptin[];
  meetingBookings: TimelineMeetingBooking[];
  totalRevenue: number;
  isClosed: boolean;
}

function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const closed = new Set(["Closed", "closed", "Close", "close", "Won", "won", "Sale", "sale"]);
  const noshow = new Set(["No Show", "Cancelled", "Rescheduled", "Cancelled by sales team"]);
  const isWon = closed.has(outcome);
  const isNoShow = noshow.has(outcome) || !outcome;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      isWon ? "bg-emerald-500/15 text-emerald-400" :
      isNoShow ? "bg-zinc-500/15 text-zinc-400" :
      "bg-amber-500/15 text-amber-400"
    }`}>
      {outcome || "No Show"}
    </span>
  );
}

// ============================================================================
// DRAWER COMPONENT
// ============================================================================

export function LeadTimelineDrawer({ email, onClose }: { email: string; onClose: () => void }) {
  const [data, setData] = useState<LeadTimelineData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetch(`/api/lead-timeline?email=${encodeURIComponent(email)}`)
      .then(r => r.json() as Promise<LeadTimelineData>)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [email]);

  const pri = branding.colors.primary;
  const sec = branding.colors.secondary;

  const events = data ? [
    ...data.optins.map(o => ({ type: "optin" as const, date: o.date, data: o })),
    ...(data.webinarOptins ?? []).map(w => ({ type: "webinar" as const, date: w.date, data: w })),
    ...(data.meetingBookings ?? []).map(m => ({ type: "meeting" as const, date: m.bookedAt, data: m })),
    ...(data.application ? [{ type: "app" as const, date: data.application.date, data: data.application }] : []),
    ...data.calls.map(c => ({ type: "call" as const, date: c.date, data: c })),
  ].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return -1;
    if (!b.date) return 1;
    return a.date.localeCompare(b.date);
  }) : [];

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      {/* Drawer */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="fixed right-0 top-0 h-full w-full max-w-[520px] bg-card border-l border-border z-50 flex flex-col overflow-hidden"
        style={{ boxShadow: "-8px 0 40px rgba(0,0,0,0.2)" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-foreground truncate">{data?.name || email}</h2>
              {data?.isClosed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Closed Won
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{email}</p>
            {data && (data.phone || data.country) && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {[data.phone, data.country].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <button onClick={onClose}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* KPI strip */}
        {data && (
          <div className="flex gap-3 px-5 py-3 border-b border-border shrink-0">
            {[
              { label: "Optins", value: data.optins.length + (data.webinarOptins?.length ?? 0) },
              { label: "Applied", value: data.application ? "Yes" : "No" },
              { label: "Meetings", value: (data.meetingBookings?.length ?? 0) + data.calls.length },
              { label: "Revenue", value: data.totalRevenue > 0 ? `$${data.totalRevenue.toLocaleString()}` : "$0" },
            ].map(k => (
              <div key={k.label} className="flex-1 min-w-0 rounded-xl px-3 py-2 text-center"
                style={{ background: `rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.06)`, border: `1px solid rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.12)` }}>
                <div className="text-[10px] text-muted-foreground font-medium mb-0.5">{k.label}</div>
                <div className="text-[15px] font-bold text-foreground tabular-nums">{String(k.value)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse mr-2" style={{ background: pri }} />
              Loading timeline…
            </div>
          )}
          {!loading && events.length === 0 && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">No events found for this lead.</div>
          )}
          {!loading && events.length > 0 && (
            <div className="relative">
              <div className="absolute left-[19px] top-3 bottom-3 w-px bg-border" />
              <div className="space-y-5">
                {events.map((ev, i) => {
                  const isOptin   = ev.type === "optin";
                  const isApp     = ev.type === "app";
                  const isCall    = ev.type === "call";
                  const isWebinar = ev.type === "webinar";
                  const isMeeting = ev.type === "meeting";

                  const isClosed = isCall && new Set(["Closed","closed","Close","close","Won","won","Sale","sale"]).has((ev.data as TimelineCall).outcome);
                  const dotColor = isOptin ? sec : isApp ? pri : isWebinar ? "#f59e0b" : isMeeting ? "#8b5cf6" : isClosed ? "#10b981" : "var(--muted-foreground)";
                  const label = isOptin ? "Opted In" : isApp ? "Submitted Application" : isWebinar ? "Webinar Registration" : isMeeting ? "Meeting Booked" : "Call";

                  return (
                    <div key={i} className="flex gap-4 relative">
                      <div className="shrink-0 w-10 flex flex-col items-center">
                        <div className="w-5 h-5 rounded-full border-2 border-background flex items-center justify-center z-10"
                          style={{ background: dotColor }}>
                          {isOptin && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2a5 5 0 110 10A5 5 0 0112 2zm0 12c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z"/></svg>}
                          {isApp && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                          {isCall && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>}
                          {isWebinar && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.07A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14v-4zM3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/></svg>}
                          {isMeeting && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0 rounded-xl border border-border/60 bg-background/60 px-4 py-3 mb-1"
                        style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-[11px] font-semibold text-foreground">{label}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{ev.date ? fmtDate(ev.date) : "—"}</span>
                        </div>

                        {isOptin && (() => {
                          const o = ev.data as TimelineOptin;
                          return (
                            <div className="space-y-0.5">
                              {o.source && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{o.source}</span></div>}
                              {o.medium && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{o.medium}</span></div>}
                              {o.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{o.campaign}</span></div>}
                              {o.content && <div className="text-[10px] text-muted-foreground">Content: <span className="text-foreground/80 truncate">{o.content}</span></div>}
                              {o.sheet && <div className="text-[10px] text-muted-foreground">Sheet: <span className="text-foreground/80">{o.sheet}</span></div>}
                            </div>
                          );
                        })()}

                        {isWebinar && (() => {
                          const w = ev.data as TimelineWebinarOptin;
                          return (
                            <div className="space-y-0.5">
                              {w.webinarName && <div className="text-[10px] text-muted-foreground">Webinar: <span className="text-foreground/80">{w.webinarName}</span></div>}
                              {w.source && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{w.source}</span></div>}
                              {w.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{w.campaign}</span></div>}
                            </div>
                          );
                        })()}

                        {isMeeting && (() => {
                          const m = ev.data as TimelineMeetingBooking;
                          return (
                            <div className="space-y-0.5">
                              {m.meetingAt && <div className="text-[10px] text-muted-foreground">Scheduled for: <span className="text-foreground/80">{fmtDate(m.meetingAt)}</span></div>}
                              {m.closer && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{m.closer}</span></div>}
                            </div>
                          );
                        })()}

                        {isApp && (() => {
                          const a = ev.data as TimelineApplication;
                          return (
                            <div className="space-y-0.5">
                              {a.source && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{a.source}</span></div>}
                              {a.medium && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{a.medium}</span></div>}
                              {a.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{a.campaign}</span></div>}
                              {a.responseType && <div className="text-[10px] text-muted-foreground">Type: <span className="text-foreground/80">{a.responseType}</span></div>}
                            </div>
                          );
                        })()}

                        {isCall && (() => {
                          const c = ev.data as TimelineCall;
                          return (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <OutcomeBadge outcome={c.outcome} />
                                {c.cash > 0 && (
                                  <span className="text-[10px] font-semibold text-emerald-400">${c.cash.toLocaleString()}</span>
                                )}
                              </div>
                              {c.offer && <div className="text-[10px] text-muted-foreground">Offer: <span className="text-foreground/80">{c.offer}</span></div>}
                              {c.closer && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{c.closer}</span></div>}
                              {c.setter && <div className="text-[10px] text-muted-foreground">Setter: <span className="text-foreground/80">{c.setter}</span></div>}
                              {c.funnel && <div className="text-[10px] text-muted-foreground">Funnel: <span className="text-foreground/80">{c.funnel}</span></div>}
                              {c.notes && <div className="text-[10px] text-muted-foreground mt-1 italic">"{c.notes}"</div>}
                              {c.fathomLink && (
                                <a href={c.fathomLink} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] mt-1"
                                  style={{ color: pri }}>
                                  View recording ↗
                                </a>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}
